/**
 * SignatureUploader
 * ─────────────────
 * Lets a master/admin attach a saved signature to a Konsulent profile, either
 * by:
 *   1. Uploading a PNG/JPEG file (the cleanest result — pre-scanned blue ink),
 *   2. Drawing directly with a signature pad in #0033A0 ink (matching the
 *      house-style used everywhere else in the CRM).
 *
 * Outputs a base64 data URL ready for `db.users.signature_dataurl`.
 * The Doc Designer render pipeline then auto-injects this image into any
 * Signature block tagged with `signerRole="berater_finma"` or
 * `signerRole="berater_ohne_finma"` belonging to this consultant.
 */
import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Upload, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const INK = "#0033A0";

export default function SignatureUploader({ value, onChange }) {
  const padRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState("preview"); // "preview" | "draw"

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      toast.error("Bitte eine PNG- oder JPEG-Datei wählen.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß (max 2 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      toast.success("Unterschrift hochgeladen.");
    };
    reader.readAsDataURL(file);
  };

  const saveDrawing = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error("Bitte zuerst unterschreiben.");
      return;
    }
    // `getTrimmedCanvas` crops whitespace so the resulting PNG fits tightly.
    const dataUrl = pad.getCanvas().toDataURL("image/png");
    onChange(dataUrl);
    setMode("preview");
    toast.success("Unterschrift gespeichert.");
  };

  return (
    <div className="border border-stone-200 rounded-md p-3 bg-white" data-testid="signature-uploader">
      {mode === "preview" ? (
        <>
          {value ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-20 border border-stone-200 rounded bg-stone-50 flex items-center justify-center overflow-hidden">
                <img src={value} alt="Unterschrift" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Button type="button" size="sm" variant="outline" onClick={() => setMode("draw")} className="gap-1.5" data-testid="sig-redraw">
                  <Pencil className="w-3.5 h-3.5" /> Neu zeichnen
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="sig-reupload">
                  <Upload className="w-3.5 h-3.5" /> Datei wechseln
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => onChange("")} className="gap-1.5 text-red-600 hover:bg-red-50" data-testid="sig-remove">
                  <Trash2 className="w-3.5 h-3.5" /> Entfernen
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="gap-1.5 flex-1" data-testid="sig-upload">
                <Upload className="w-4 h-4" /> Datei hochladen
              </Button>
              <Button type="button" size="sm" onClick={() => setMode("draw")} className="gap-1.5 flex-1 bg-prime hover:bg-prime/90 text-white" data-testid="sig-draw">
                <Pencil className="w-4 h-4" /> Zeichnen
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2">
          <div className="border-2 border-dashed border-stone-300 rounded bg-stone-50">
            <SignatureCanvas
              ref={padRef}
              penColor={INK}
              canvasProps={{
                width: 480,
                height: 140,
                className: "w-full h-32 bg-white rounded",
                "data-testid": "sig-pad",
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => padRef.current?.clear()} data-testid="sig-clear">
              Leeren
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setMode("preview")} data-testid="sig-cancel">
              Abbrechen
            </Button>
            <Button type="button" size="sm" onClick={saveDrawing} className="gap-1.5 bg-prime hover:bg-prime/90 text-white ml-auto" data-testid="sig-save">
              <Check className="w-4 h-4" /> Speichern
            </Button>
          </div>
        </div>
      )}
      <input
        type="file"
        ref={fileRef}
        className="hidden"
        accept="image/png,image/jpeg"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
